// Normalizes the backend error envelope into a single readable shape the UI can
// render without hiding anything. Backend envelope (app.ts):
//   { error: { statusCode, code?, message, requestId, ...details } }

export interface NormalizedError {
  /** HTTP status, or 0 for a transport/network failure. */
  status: number;
  /** Machine-readable code: backend `code`, or 'network_error' / 'http_error'. */
  code: string;
  /** Human-readable message (never swallowed). */
  message: string;
  requestId?: string;
  /** Any extra fields the backend attached (e.g. cooldown_remaining_seconds). */
  details?: Record<string, unknown>;
}

export class ApiError extends Error implements NormalizedError {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly details?: Record<string, unknown>;

  constructor(e: NormalizedError) {
    super(e.message);
    this.name = 'ApiError';
    this.status = e.status;
    this.code = e.code;
    this.requestId = e.requestId;
    this.details = e.details;
  }
}

/** Build a NormalizedError from an HTTP status + a (possibly malformed) JSON body. */
export function normalizeError(status: number, body: unknown): NormalizedError {
  const envelope =
    body && typeof body === 'object' && 'error' in body
      ? (body as { error: unknown }).error
      : undefined;

  if (envelope && typeof envelope === 'object') {
    const e = envelope as Record<string, unknown>;
    const { statusCode, code, message, requestId, ...rest } = e;
    return {
      status: typeof statusCode === 'number' ? statusCode : status,
      code: typeof code === 'string' ? code : 'http_error',
      message: typeof message === 'string' ? message : `Request failed (${status})`,
      requestId: typeof requestId === 'string' ? requestId : undefined,
      details: Object.keys(rest).length > 0 ? rest : undefined,
    };
  }

  return {
    status,
    code: 'http_error',
    message: typeof body === 'string' && body.length > 0 ? body : `Request failed (${status})`,
  };
}

/** Wrap a thrown fetch/transport error as a NormalizedError. */
export function networkError(err: unknown): NormalizedError {
  const message = err instanceof Error ? err.message : 'Network request failed';
  return { status: 0, code: 'network_error', message };
}
